import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch, ofePost } from './useOFE';

const STATUS_LABELS = {
  proposed: { label: 'Proposed', color: 'text-yellow-400' },
  approved: { label: 'Approved', color: 'text-blue-400' },
  executed: { label: 'Executed', color: 'text-green-400' },
  rejected: { label: 'Rejected', color: 'text-red-400' },
};

export default function OFEReallocationWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const [executing, setExecuting] = useState(false);
  const [err, setErr] = useState(null);

  const { data: realloc, loading, error, reload } = useOFEFetch(`/ofe/reallocations/${id}`, user, [id]);
  const { data: impact } = useOFEFetch(`/ofe/reallocations/${id}/impact`, user, [id]);

  const handleExecute = async () => {
    if (!window.confirm('Execute this reallocation? This will move all listed items to the target project and trigger a JE proposal.')) return;
    setExecuting(true);
    setErr(null);
    try {
      await ofePost(`/ofe/reallocations/${id}/execute`, { policy_id: 'OFE-REALLOC-001' }, user);
      reload();
    } catch (e) {
      setErr(e.message);
    } finally {
      setExecuting(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading reallocation...</div>;
  if (error || !realloc) return <div className="p-6 text-red-400">{error || 'Not found'}</div>;

  const st = STATUS_LABELS[realloc.status] || { label: realloc.status, color: 'text-gray-300' };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        <Link to="/ofe/reallocations" className="text-gray-400 hover:text-white text-sm">← Reallocations</Link>
        <h1 className="text-xl font-bold text-white">Reallocation {realloc.reallocation_number}</h1>
        <span className={`text-sm font-semibold ${st.color}`}>{st.label}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
          <h3 className="text-white font-semibold">Details</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-gray-400">From Project</dt><dd className="text-red-400 font-mono">{realloc.from_project}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-400">To Project</dt><dd className="text-green-400 font-mono">{realloc.to_project}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-400">Vendor Approval</dt><dd className="text-white">{realloc.vendor_approval_required ? 'Required' : 'Not required'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-400">Policy</dt><dd className="text-gray-300 font-mono text-xs">{realloc.policy_id || '—'}</dd></div>
            <div className="flex justify-between"><dt className="text-gray-400">Proposed</dt><dd className="text-gray-300 text-xs">{realloc.proposed_at?.slice(0, 16)}</dd></div>
            {realloc.executed_at && <div className="flex justify-between"><dt className="text-gray-400">Executed</dt><dd className="text-green-400 text-xs">{realloc.executed_at?.slice(0, 16)}</dd></div>}
          </dl>
          <div className="border-t border-gray-700 pt-3">
            <div className="text-gray-400 text-xs mb-1">Reason</div>
            <p className="text-gray-300 text-sm">{realloc.reason}</p>
          </div>
        </div>

        {impact && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
            <h3 className="text-white font-semibold">Impact Analysis</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-400">Items to Move</dt><dd className="text-white font-bold">{impact.item_count}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Affected Vendors</dt><dd className="text-white">{impact.affected_vendors?.length || 0}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Proposed JE Lines</dt><dd className="text-purple-400">{impact.proposed_je_lines}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-400">Schedule Risk</dt>
                <dd className={impact.schedule_risk === 'high' ? 'text-red-400' : impact.schedule_risk === 'medium' ? 'text-yellow-400' : 'text-green-400'}>
                  {impact.schedule_risk}
                </dd>
              </div>
              {impact.financial_exposure && (
                <div className="flex justify-between"><dt className="text-gray-400">Financial Exposure</dt><dd className="text-yellow-400">${impact.financial_exposure.toLocaleString()}</dd></div>
              )}
            </dl>
          </div>
        )}
      </div>

      {err && <div className="bg-red-900/20 border border-red-700 rounded p-3 text-red-400 text-sm">{err}</div>}

      {realloc.status === 'proposed' && (
        <div className="flex gap-3">
          <button onClick={handleExecute} disabled={executing}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-semibold">
            {executing ? 'Executing...' : 'Execute Reallocation'}
          </button>
          <Link to={`/ofe/reallocations/${id}/approve?type=director`}
            className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">
            Submit Approval
          </Link>
        </div>
      )}
    </div>
  );
}
